import React, { useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import classNames from 'classnames'
import { twMerge } from 'tailwind-merge'
import { ArrowLeftIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FormProviderV2 } from '@/@diffrag/form/FormProviderV2'
import { toast } from '@/lib/toast'
import {
  SubmitErrorHandler,
  useFormContext,
  UseFormReturn,
} from 'react-hook-form'
import { cn } from '@/lib/utils'
import { Box, Flex } from '@/@diffrag/components/base'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'

type CustomButton = {
  label: string
  onClick: () => void | Promise<void>
  variant?:
    | 'default'
    | 'destructive'
    | 'outline'
    | 'secondary'
    | 'ghost'
    | 'link'
  className?: string
}

type Props<T> = {
  breadcrumb?: React.ReactNode
  children?: React.ReactNode
  title: string
  className?: string
  data?: T
  onSave?: (data: T) => Promise<void>
  onDelete?: () => Promise<void>
  childrenWrapClassName?: string
  hideSaveToast?: (data: T) => boolean
  saveDisabled?: boolean
  cancel?: boolean
  customButtons?: CustomButton[]
}

export const ContentDetailView = <T,>({
  children,
  title,
  className,
  data,
  onSave,
  onDelete,
  childrenWrapClassName,
  hideSaveToast,
  saveDisabled,
  breadcrumb,
  cancel,
  customButtons,
}: Props<T>) => {
  const form = useRef<UseFormReturn>()

  const navigate = useNavigate()

  const isShowButtons = !!(onSave || onDelete || customButtons?.length)

  const handleFormError = useCallback((errors: any) => {
    const firstError = Object.keys(errors)[0]

    const getErrorMessage = (error: any): string | null => {
      if (error && typeof error === 'object') {
        console.log(error)
        if (error.message) {
          return error.message
        }
        for (const key in error) {
          const message = getErrorMessage(error[key])
          if (message) {
            console.log(error, key)
            return message
          }
        }
      }
      return null
    }

    console.log(errors)

    toast.error(getErrorMessage(errors) ?? '항목을 올바르게 입력해주세요.')
  }, [])

  const onClickSave = useCallback(() => {
    form.current?.handleSubmit(async (data) => {
      if (!onSave) {
        return
      }

      const processFiles = async (obj: any): Promise<any> => {
        if (Array.isArray(obj)) {
          for (let i = 0; i < obj.length; i++) {
            obj[i] = await processFiles(obj[i])
          }
          return obj
        } else if (obj && typeof obj === 'object' && obj !== null) {
          if ('file' in obj && 'uploadHandler' in obj) {
            return await obj.uploadHandler?.(obj)
          } else {
            for (const key of Object.keys(obj)) {
              obj[key] = await processFiles(obj[key])
            }
            return obj
          }
        }
        return obj
      }

      await processFiles(data)

      try {
        await onSave(data as T)
        if (!hideSaveToast?.(data as T)) {
          toast.success('저장되었습니다.')
        }
      } catch (error) {
        if (error instanceof Response) {
          toast.error(`${error.error?.message}`, 2000)
        } else {
          toast.error(`저장 실패 ${JSON.stringify(error)}`)
        }

        throw error
      }
    }, handleFormError)()
  }, [onSave, handleFormError, hideSaveToast])

  const onClickDelete = useCallback(async () => {
    if (onDelete) {
      await onDelete()
      navigate(-1)
      toast.success('삭제되었습니다.')
    }
  }, [onDelete])

  const onLoadForm = useCallback((_form: UseFormReturn) => {
    form.current = _form
  }, [])

  return (
    <FormProviderV2 {...form} data={data} onLoadForm={onLoadForm}>
      <div
        className={twMerge(
          classNames(
            className,
            'flex flex-col flex-1 gap-4 bg-slate-100 pt-4 px-6 pb-20  min-h-[calc(100dvh-48px)]',
          ),
        )}
      >
        {breadcrumb}
        <Box
          as={onSave ? 'form' : 'div'}
          id="form"
          className={cn('rounded-md bg-white', childrenWrapClassName)}
        >
          <Flex align="center" className="border-b border-slate-100 px-4 py-3">
            {title}
          </Flex>
          <Box className="p-4">{children}</Box>
        </Box>
      </div>

      {isShowButtons && (
        <div className="fixed bottom-0 z-10 flex h-16 w-[calc(100vw-240px)] items-center justify-end gap-2 border-t bg-white px-6">
          {cancel && (
            <Button
              className="w-[100px]"
              variant="outline"
              onClick={() => {
                navigate(-1)
              }}
            >
              취소
            </Button>
          )}

          {/* 커스텀 버튼들 */}
          {customButtons?.map((button, index) => (
            <Button
              key={index}
              type="button"
              variant={button.variant || 'outline'}
              className={button.className || 'w-[100px]'}
              onClick={button.onClick}
            >
              {button.label}
            </Button>
          ))}

          <div>
            {onDelete && (
              <Dialog>
                <DialogTrigger>
                  <Button size="sm" variant="destructive">
                    삭제
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>삭제 확인</DialogTitle>
                  </DialogHeader>
                  <DialogDescription className="mt-4">
                    데이터를 삭제하시겠습니까?
                  </DialogDescription>
                  <DialogFooter>
                    <DialogClose>
                      <Button variant="outline">취소</Button>
                    </DialogClose>
                    <Button
                      variant="destructive"
                      type="button"
                      onClick={onClickDelete}
                    >
                      삭제하기
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
          <div>
            {onSave && (
              <Button
                type="button"
                className="w-[100px] bg-[#167CF9] hover:bg-[#167CF9]/90"
                disabled={saveDisabled}
                onClick={onClickSave}
              >
                저장
              </Button>
            )}
          </div>
        </div>
      )}
    </FormProviderV2>
  )
}
